import type { AdoptSettings } from "../../api/types";
import { MONTHS } from "../../api/types";
import { t, tDynamic } from "../../i18n/t";

/**
 * xmg-orgadopt. The consent sentence for "an EMPTY LuciBook organisation takes its settings from the source":
 * "These settings will be set on [org] from [Vendor]: Financial year end 28 Feb (now 1 Jan); VAT: Standard
 * accounting (now off); VAT number 470342805 (now none)". Rendered from the structured record (never parsed out of
 * the server's English sentence) so every value goes through `t()`; used by the wizard's consent step (a preview,
 * nothing written) and by the failed-preflight status page (the same words, then "Set these settings and retry").
 */

type Setting = AdoptSettings["settings"][number];

/** `02-28` -> `28 Feb`. A value that is not `MM-DD` is shown as it came. */
export function formatFyEnd(value: string | null | undefined): string {
  const match = value === null || value === undefined ? null : /^(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return value ?? t("adopt.none");
  const month = MONTHS[Number.parseInt(match[1] ?? "", 10) - 1];
  return month === undefined ? (value ?? t("adopt.none")) : `${Number.parseInt(match[2] ?? "", 10)} ${month}`;
}

function schemeLabel(scheme: string | null | undefined, flatRate: string | null | undefined): string {
  if (scheme === null || scheme === undefined) return t("adopt.none");
  const label = tDynamic("adopt.scheme.", scheme);
  return scheme === "FLAT_RATE_ACCRUAL" && flatRate !== null && flatRate !== undefined
    ? `${label} ${flatRate}%`
    : label;
}

function frequencyLabel(frequency: string | null | undefined): string {
  return frequency === null || frequency === undefined
    ? t("adopt.none")
    : tDynamic("adopt.frequency.", frequency);
}

/** One human line per setting. The flat-rate percentage is folded into the VAT line, never listed alone. */
export function adoptLines(adopt: AdoptSettings): { key: string; text: string }[] {
  const flat: Setting | undefined = adopt.settings.find((s) => s.key === "flat_rate_percent");
  const lines: { key: string; text: string }[] = [];
  for (const setting of adopt.settings) {
    if (setting.key === "fy_end") {
      lines.push({
        key: setting.key,
        text: t("adopt.fy_end", {
          source: formatFyEnd(setting.source),
          destination: formatFyEnd(setting.destination),
        }),
      });
    } else if (setting.key === "vat_scheme") {
      lines.push({
        key: setting.key,
        text: t("adopt.vat_scheme", {
          source: schemeLabel(setting.source, flat?.source),
          destination: schemeLabel(setting.destination, flat?.destination),
        }),
      });
    } else if (setting.key === "vat_filing_frequency") {
      lines.push({
        key: setting.key,
        text: t("adopt.vat_filing_frequency", {
          source: frequencyLabel(setting.source),
          destination: frequencyLabel(setting.destination),
        }),
      });
    } else if (setting.key === "vat_number") {
      lines.push({
        key: setting.key,
        text: t("adopt.vat_number", {
          source: setting.source ?? t("adopt.none"),
          destination: setting.destination ?? t("adopt.none"),
        }),
      });
    }
  }
  return lines;
}

export function AdoptSettingsNotice({
  adopt,
  testId = "adopt-settings",
}: {
  adopt: AdoptSettings;
  testId?: string;
}): JSX.Element {
  return (
    <div data-testid={testId}>
      <p>{t("adopt.intro", { destination: adopt.destination_name, vendor: adopt.vendor })}</p>
      <ul>
        {adoptLines(adopt).map((line) => (
          <li key={line.key}>{line.text}</li>
        ))}
      </ul>
    </div>
  );
}
